import React, { useState, useEffect } from 'react';
import { getProviders, signIn } from 'next-auth/react';
import { useRouter } from 'next/router';
import Tippy from '@tippyjs/react';
import QRCode from 'qrcode';
import { FaApple, FaGooglePlay } from 'react-icons/fa';

import ForgotPassword from 'views/auth/forgot-password';
import Logo from 'comps/icons/saLogo';
import Notifications from 'libs/notifications';
import { getAuthReasonMessage } from 'libs/authMessages';
import Authentication from 'models/authentication';
import ConfiguredHeaderMessage from 'comps/configuredHeaderMessage';
import { getRecaptchaToken } from 'libs/recaptcha';

const ROLE_OPTIONS = {
    client: { label: 'Login as Client', provider: 'client' },
	linguist: { label: 'Login as Linguist', provider: 'translator' },
	project_manager: { label: 'Login as Project Manager', provider: 'credentials' },
};

const Policy = () => (
	<div className="policy mt-2">
		<span>By signing up, you agree to our <a target="_blank" rel="noreferrer" href="https://robertcat.ai/">Terms of Service</a> and <a target="_blank" rel="noreferrer" href="https://robertcat.ai/privacy-policy-cat">Privacy Policy</a></span>
	</div>
);

const UnifiedSignIn = ({ onSwitchToClassic }) => {
	const [email, setEmail] = useState('');
	const [password, setPassword] = useState('');
	const [isPasswordVisible, setPasswordVisibility] = useState(false);
	const [code, setCode] = useState('');
	const [withPassword, setWithPassword] = useState(true);
	const [codeFieldVisible, setCodeFieldVisible] = useState(false);
	const [matchingRoles, setMatchingRoles] = useState([]);
	const [linguistOrganizations, setLinguistOrganizations] = useState([]);
	const [selectingOrganization, setSelectingOrganization] = useState(false);
    const [clientOrganizations, setClientOrganizations] = useState([]);
    const [organizationRole, setOrganizationRole] = useState('linguist');
	const [forgotPasswordForm, setForgotPasswordForm] = useState(false);
	const [authError, setAuthError] = useState('');
	// Most authError banners are real failures (red), but "please set up / enter
	// your authenticator code" are routine next steps, not something the user
	// did wrong - shown in green instead so they don't look like an error.
	const [authErrorIsInfo, setAuthErrorIsInfo] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [googleAvailable, setGoogleAvailable] = useState(false);
	const [googleSelection, setGoogleSelection] = useState(null);
	const [authenticator, setAuthenticator] = useState(null);
	const [authenticatorCode, setAuthenticatorCode] = useState('');
	const [authenticatorQrCode, setAuthenticatorQrCode] = useState('');
	const router = useRouter();
	const callbackUrl = router?.query?.callbackUrl ?? process.env.NEXT_PUBLIC_FRONTEND_URL;
	const isRedirectReasonError = typeof router?.query?.reason === 'string' && router.query.reason.startsWith('google-');
	const redirectReasonMessage = isRedirectReasonError ? '' : getAuthReasonMessage(router?.query?.reason);
	const redirectReasonErrorMessage = isRedirectReasonError ? getAuthReasonMessage(router?.query?.reason) : '';
	const redirectErrorMessage = getAuthReasonMessage(router?.query?.error);

	React.useEffect(() => {
		getProviders()
			.then((providers) => setGoogleAvailable(Boolean(providers?.google)))
			.catch(() => setGoogleAvailable(false));
	}, []);

	React.useEffect(() => {
		if (!router?.query?.googleSelection) {
			setGoogleSelection(null);
			return;
		}

		setIsLoading(true);
		fetch('/api/auth/google-options')
			.then((response) => response.json())
			.then((result) => {
				if (!result?.resp || !Array.isArray(result.options) || result.options.length === 0) {
					setAuthErrorIsInfo(false);
					setAuthError(getAuthReasonMessage(result?.errorCode || 'google-sign-in-failed'));
					setGoogleSelection(null);
					return;
				}

				setAuthErrorIsInfo(false);
				setAuthError('');
				setGoogleSelection(result);
			})
			.catch(() => {
				setAuthErrorIsInfo(false);
				setAuthError(getAuthReasonMessage('google-sign-in-failed'));
				setGoogleSelection(null);
			})
			.finally(() => setIsLoading(false));
	}, [router?.query?.googleSelection]);

	const signInWithGoogle = () => {
		setAuthErrorIsInfo(false);
		setAuthError('');
		setIsLoading(true);
		signIn('google', { callbackUrl });
	};

	const loginWithGoogleSelection = async (option) => {
		setIsLoading(true);
		setAuthErrorIsInfo(false);
		setAuthError('');
		try {
			const result = await signIn('google-selection', {
				role: option.role,
				companyId: option.companyId,
				callbackUrl,
				redirect: false,
			});

			if (result?.error) {
				setAuthError(getAuthReasonMessage(result.error));
				return;
			}
			if (result?.url) router.push(result.url);
		} catch (error) {
			setAuthError(getAuthReasonMessage('google-sign-in-failed'));
		} finally {
			setIsLoading(false);
		}
	};

	useEffect(() => {
		let active = true;
		if (!authenticator?.uri) {
			setAuthenticatorQrCode('');
			return () => { active = false; };
		}

		QRCode.toDataURL(authenticator.uri, { width: 220, margin: 2 })
			.then((dataUrl) => { if (active) setAuthenticatorQrCode(dataUrl); })
			.catch(() => { if (active) setAuthenticatorQrCode(''); });

		return () => { active = false; };
	}, [authenticator?.uri]);

	const loginAs = async (role, oneTimePassword = '', companyId = null) => {
		const option = ROLE_OPTIONS[role];
		if (!option) return;

		setIsLoading(true);
		setAuthErrorIsInfo(false);
		setAuthError('');
        try {
            if (role === 'client') {
                const recaptchaToken = await getRecaptchaToken('login');
                const result = await signIn('client', { email, password, companyId, recaptchaToken, callbackUrl: '/portal', redirect: false });
                if (result?.error) setAuthError(getAuthReasonMessage(result.error));
                else if (result?.url) router.push(result.url);
                return;
            }
            // One token, reused for both the direct check below and the
			// signIn() call further down - both happen within the same
			// click, and reCAPTCHA v3 doesn't require a new token per call.
			const recaptchaToken = await getRecaptchaToken('login');
			const preflightResponse = role === 'project_manager'
				? await Authentication.login(email, password, authenticatorCode, authenticator?.secret || '', recaptchaToken)
				: await Authentication.getTokenForTranslator({ email, ...(oneTimePassword ? { code: oneTimePassword } : { password }), companyId, authenticatorCode, authenticatorSecret: authenticator?.secret || '', recaptchaToken });
			const preflight = role === 'project_manager' ? preflightResponse : preflightResponse?.resp;
			if (!preflight?.resp) {
				if (['authenticator-setup-required', 'authenticator-code-required', 'authenticator-code-invalid'].includes(preflight?.errorCode)) {
					setAuthenticator((current) => ({
						secret: preflight.authenticatorSecret || current?.secret || '',
						uri: preflight.authenticatorUri || current?.uri || '',
						isSetup: preflight.errorCode === 'authenticator-setup-required' || current?.isSetup,
						role, companyId, emailCode: oneTimePassword,
					}));
					setAuthErrorIsInfo(preflight.errorCode !== 'authenticator-code-invalid');
					setAuthError(preflight.message || 'Enter the 6-digit code from your authenticator app.');
					return;
				}
				setAuthErrorIsInfo(false);
				setAuthError(preflight?.message || 'We could not sign you in. Please try again.');
				return;
			}
			const result = await signIn(option.provider, {
				email,
				...(role === 'project_manager'
					? { pass: password, ...(preflight.loginTicket ? { loginTicket: preflight.loginTicket } : {}) }
					: oneTimePassword ? { code: oneTimePassword } : { password }),
				...(authenticator ? { authenticatorCode, authenticatorSecret: authenticator.secret } : {}),
				...(companyId ? { companyId } : {}),
				recaptchaToken,
				callbackUrl,
				redirect: false,
			});

			if (result?.error) {
				setAuthError(getAuthReasonMessage(result.error));
				return;
			}
			if (result?.url) router.push(result.url);
		} catch (error) {
			setAuthError('We could not sign you in. Please try again.');
		} finally {
			setIsLoading(false);
		}
	};

	const submitPassword = async (event) => {
		event.preventDefault();
		setAuthErrorIsInfo(false);
		setAuthError('');
		setMatchingRoles([]);
		setLinguistOrganizations([]);
		setSelectingOrganization(false);
		setIsLoading(true);
		try {
			const recaptchaToken = await getRecaptchaToken('login');
			const response = await fetch('/api/ext/auth/check-credentials', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ email, password, recaptchaToken }),
			});
			const result = await response.json();
			const roles = Array.isArray(result?.roles) ? result.roles : [];
			const linguistOrgs = Array.isArray(result?.linguistOrganizations) ? result.linguistOrganizations : [];
            const clientOrgs = Array.isArray(result?.clientOrganizations) ? result.clientOrganizations : [];
            setClientOrganizations(clientOrgs);
            setLinguistOrganizations(linguistOrgs);
            const selectedRole = roles[0] === 'client' ? 'client' : 'linguist';
            const organizations = selectedRole === 'client' ? clientOrgs : linguistOrgs;
			if (roles.length === 0) {
				setAuthError('The email or password is incorrect. Please try again.');
				return;
			}
			if (roles.length === 1 && ['linguist', 'client'].includes(roles[0]) && organizations.length > 1) {
				setOrganizationRole(selectedRole);
                setSelectingOrganization(true);
				return;
			}
			if (roles.length === 1) {
				await loginAs(roles[0], '', organizations[0]?.id);
				return;
			}
			setMatchingRoles(roles);
		} catch (error) {
			setAuthError('We could not sign you in. Please try again.');
		} finally {
			setIsLoading(false);
		}
	};

	const sendOneTimePassword = async () => {
		if (!email) return;
		setIsLoading(true);
		try {
			const response = await fetch('/api/ext/users/email-code', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ email }),
			});
			const result = await response.json();
			if (result?.error) throw result;
			setCodeFieldVisible(true);
			Notifications.success('One-Time Password Sent. Check your email.');
		} catch (error) {
			Notifications.error(error?.error || 'We could not send a one-time password.');
		} finally {
			setIsLoading(false);
		}
	};

	const switchToOtp = () => {
		setWithPassword(false);
		setCodeFieldVisible(false);
		setCode('');
		setAuthErrorIsInfo(false);
		setAuthError('');
	};
	const switchToPassword = () => {
		setWithPassword(true);
		setCodeFieldVisible(false);
		setCode('');
		setAuthErrorIsInfo(false);
		setAuthError('');
	};

	return (
		<div className="viewLogin">
			<div className="leftSide" />
			<div className="rightSide">
				<div className="logoCnt"><Logo /></div>
				<div className="signInMessage">
					<ConfiguredHeaderMessage configKey="sign_in_message" authDatabase />
				</div>
				<div className="unifiedLogin">
					{forgotPasswordForm ? <ForgotPassword signIn={() => setForgotPasswordForm(false)} type="linguist" /> : <>
						{redirectReasonMessage && <div className="authNotice authNoticeInfo">{redirectReasonMessage}</div>}
						{redirectReasonErrorMessage && <div className="authNotice authNoticeError">{redirectReasonErrorMessage}</div>}
						{redirectErrorMessage && <div className="authNotice authNoticeError">{redirectErrorMessage}</div>}
						{authError && <div className={`authNotice ${authErrorIsInfo ? 'authNoticeInfo' : 'authNoticeError'}`}>{authError}</div>}
						{googleSelection ? (
							<div className="accountChoice">
								<p>Choose how you want to continue with Google.</p>
								<div className="roleChoices organizationChoices">
									{googleSelection.options.map((option) => (
										<button key={`${option.role}-${option.companyId}`} type="button" className="roleChoice" onClick={() => loginWithGoogleSelection(option)} disabled={isLoading}>
											{option.name}
										</button>
									))}
								</div>
								<button type="button" className="backLink" onClick={() => { setGoogleSelection(null); router.replace('/sign-in', undefined, { shallow: true }); }}>Use different sign-in method</button>
							</div>
						) : selectingOrganization ? (
							<div className="accountChoice">
								<p>Which organization would you like to access?</p>
								<div className="roleChoices organizationChoices">
									{(organizationRole === 'client' ? clientOrganizations : linguistOrganizations).map((organization) => <button key={organization.id} type="button" className="roleChoice" onClick={() => loginAs(organizationRole, '', organization.id)} disabled={isLoading}>{organization.name}</button>)}
								</div>
								<button type="button" className="backLink" onClick={() => { setSelectingOrganization(false); setMatchingRoles([]); }}>Use different credentials</button>
							</div>
						) : matchingRoles.length > 1 ? (
							<div className="accountChoice">
								<p>Which role would you like to use?</p>
								<div className="roleChoices">
									{matchingRoles.map((role) => <button key={role} type="button" className="roleChoice" onClick={() => {
                                        const organizations = role === 'client' ? clientOrganizations : linguistOrganizations;
                                        if (role !== 'project_manager' && organizations.length > 1) { setOrganizationRole(role); setSelectingOrganization(true); }
                                        else loginAs(role, '', organizations[0]?.id);
                                    }} disabled={isLoading}>{ROLE_OPTIONS[role]?.label}</button>)}
								</div>
								<button type="button" className="backLink" onClick={() => setMatchingRoles([])}>Use different credentials</button>
							</div>
						) : authenticator ? (
							<form onSubmit={(event) => { event.preventDefault(); loginAs(authenticator.role, authenticator.emailCode, authenticator.companyId); }}>
								<h4>{authenticator.isSetup ? 'Set up your authenticator' : 'Enter your authenticator code'}</h4>
								{authenticator.isSetup && <>
									<ol className="authenticatorSteps">
										<li>
											<strong>Download Google Authenticator</strong>
											<div className="authenticatorStoreButtons">
												<a href="https://apps.apple.com/app/google-authenticator/id388497605" target="_blank" rel="noreferrer" className="storeButton"><FaApple /><span><small>Download on the</small>App Store</span></a>
												<a href="https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2" target="_blank" rel="noreferrer" className="storeButton"><FaGooglePlay /><span><small>GET IT ON</small>Google Play</span></a>
											</div>
										</li>
										<li>Open the app, tap <strong>+</strong>, then choose <strong>Scan a QR code</strong>.</li>
										<li>Scan the QR code below.</li>
										<li>Enter the current 6-digit code from the app and click <strong>Confirm setup</strong>.</li>
									</ol>
									{authenticatorQrCode && <img className="authenticatorQrCode" src={authenticatorQrCode} alt="Google Authenticator setup QR code" />}
									<p className="authenticatorHelp">Cannot scan it? Choose <strong>Enter a setup key</strong>, select <strong>Time based</strong>, and use:</p>
									<code className="authenticatorSecret">{authenticator.secret}</code>
									<p className="authenticatorHelp">Account: {email}</p>
								</>}
								<input type="text" inputMode="numeric" className="form-control mt-3 custom-input" placeholder="Enter 6-digit authenticator code" value={authenticatorCode} onChange={(event) => setAuthenticatorCode(event.target.value.replace(/\D/g, '').slice(0, 6))} autoComplete="one-time-code" required />
								<button type="submit" className="btn btn-primary send-code-btn mt-4" disabled={isLoading}>{authenticator.isSetup ? 'Confirm setup' : 'Log in'}</button>
								<button type="button" className="backLink mt-3" onClick={() => { setAuthenticator(null); setAuthenticatorCode(''); }}>Use different credentials</button>
							</form>
						) : (
							<form onSubmit={withPassword ? submitPassword : (event) => { event.preventDefault(); codeFieldVisible ? loginAs('linguist', code) : sendOneTimePassword(); }}>
								{!codeFieldVisible && <input type="email" className="form-control mt-4 custom-input" placeholder="Enter email" value={email} onChange={(event) => setEmail(event.target.value)} autoComplete="username" required />}
								{withPassword ? <>
									<div className="passwordField">
										<input type={isPasswordVisible ? 'text' : 'password'} className="form-control mt-3 custom-input" placeholder="Enter password" value={password} onChange={(event) => setPassword(event.target.value)} autoComplete="current-password" required />
										<button type="button" className="passwordVisibility" onClick={() => setPasswordVisibility(!isPasswordVisible)} aria-label={isPasswordVisible ? 'Hide password' : 'Show password'} title={isPasswordVisible ? 'Hide password' : 'Show password'}><i className={isPasswordVisible ? 'icon-eye' : 'icon-eye-off'} /></button>
									</div>
									<div className="formLinks">
										<button type="button" className="inlineLink" onClick={() => setForgotPasswordForm(true)}>Forgot your password?</button>
										<div><button type="button" className="inlineLink accountHintText" onClick={() => setForgotPasswordForm(true)}>Don't have a password yet? Create one!</button></div>
									</div>
								</> : codeFieldVisible && <>
									<input type="text" className="form-control mt-2 custom-input" placeholder="Enter 6-digits" value={code} onChange={(event) => setCode(event.target.value)} required />
									<Tippy content={<span>Didn't receive the email? Click 'Resend OTP' for a new code.</span>} placement="right" theme="sa"><button type="button" className="resendLink" onClick={sendOneTimePassword}>Resend a One-Time Password</button></Tippy>
								</>}
								<button type="submit" className="btn btn-primary send-code-btn mt-4" disabled={isLoading}>{withPassword || codeFieldVisible ? 'Log in' : 'Next'}</button>
							</form>
						)}
						{matchingRoles.length === 0 && <>
							<div className="divider"><span>OR</span></div>
							<button type="button" className="btn btn-outline-primary custom-input custom-input-otp" onClick={withPassword ? switchToOtp : switchToPassword}>{withPassword ? 'USE ONE-TIME PASSWORD' : 'LOGIN WITH PASSWORD'}</button>
						</>}
						<Policy />
						<button type="button" className="classicLink" onClick={onSwitchToClassic}>Switch to classic login</button>
						{googleAvailable && !matchingRoles.length && !selectingOrganization && !googleSelection && (
							<button type="button" className="googleButton custom-input" onClick={signInWithGoogle} disabled={isLoading}>
								<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 25 25" className="googleMark" aria-hidden="true">
									<path fill="#4285f4" fillRule="evenodd" d="M22.1 12.723c0-.709-.064-1.39-.182-2.045H12.5v3.868h5.382a4.6 4.6 0 0 1-1.996 3.018v2.51h3.232c1.891-1.742 2.982-4.305 2.982-7.35" clipRule="evenodd" />
									<path fill="#34a853" fillRule="evenodd" d="M12.498 22.501c2.7 0 4.964-.895 6.618-2.422l-3.232-2.51c-.895.6-2.04.955-3.386.955-2.605 0-4.81-1.759-5.596-4.123h-3.34v2.591a10 10 0 0 0 8.936 5.51" clipRule="evenodd" />
									<path fill="#fbbc05" fillRule="evenodd" d="M6.905 14.397c-.2-.6-.314-1.241-.314-1.9 0-.66.114-1.3.314-1.9V8.006H3.564a10 10 0 0 0-1.064 4.49c0 1.614.386 3.142 1.064 4.492z" clipRule="evenodd" />
									<path fill="#ea4335" fillRule="evenodd" d="M12.498 6.476c1.468 0 2.786.505 3.823 1.496l2.868-2.868c-1.732-1.614-3.996-2.605-6.691-2.605-3.91 0-7.291 2.24-8.936 5.51l3.34 2.59c.787-2.364 2.991-4.123 5.596-4.123" clipRule="evenodd" />
								</svg>
								Continue with Google
							</button>
						)}
					</>}
				</div>
			</div>

			<style jsx global>{`
				@import 'scss/commons';
				.form-control, .form-control::placeholder, .form-control:focus { background: transparent !important; color: #66AE7C !important; border-color: #66AE7C !important; font-family: 'AvenirMedium', sans-serif; }
				.btn-primary, .send-code-btn { background: #66AE7C !important; border-color: #66AE7C !important; color: #fff !important; }
			`}</style>
			<style jsx>{`
				@import 'scss/base';
				.viewLogin { display: flex; min-height: 100vh; background: $body-bg url('/img/loginBGnew.png') left/cover no-repeat; background-blend-mode: multiply; }
				.leftSide { flex: 1; display: none; }
				.rightSide { flex: 1; display: flex; flex-flow: column nowrap; align-items: center; justify-content: center; }
				.logoCnt { width: 100%; display: flex; justify-content: center; margin: 0 auto 30px; }
				.signInMessage { width: 400px; max-width: calc(100vw - 32px); text-align: center; }
				.unifiedLogin { width: 400px; max-width: calc(100vw - 32px); text-align: center; }
				form { display: flex; flex-direction: column; align-items: center; }
				.custom-input { width: 400px; max-width: 100%; }
				.passwordField { position: relative; width: 400px; max-width: 100%; }
				.passwordVisibility { position: absolute; top: 1rem; right: .45rem; border: 0; background: transparent; color: #66AE7C; font-size: 1.2rem; }
				.formLinks { width: 400px; text-align: right; margin-top: 8px; }
				.inlineLink, .classicLink, .backLink, .resendLink { border: 0; background: transparent; color: #66AE7C; cursor: pointer; padding: 0; }
				.inlineLink:hover, .classicLink:hover, .backLink:hover, .resendLink:hover { text-decoration: underline; }
				.accountHintText { margin-top: 4px; }
				.resendLink { display: block; margin: 8px 0 0 auto; color: #babbbc; }
				.divider { display: flex; align-items: center; gap: 12px; margin: 18px 0 14px; color: #8e8e8e; font-size: 12px; font-weight: 600; letter-spacing: .08em; }
				.divider::before, .divider::after { content: ''; flex: 1; height: 1px; background: rgba(102,174,124,.35); }
				.custom-input-otp { border-radius: 10px; border: 1px solid rgba(102,174,124,.45) !important; }
				.policy { margin: 18px 0 0; color: #686767; text-align: center; }
				.policy a { color: #66AE7C; }
				.classicLink { display: block; margin: 10px auto 0; }

                .googleButton {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 10px;
                    margin: 40px auto 0;
                    min-height: 39px;
					max-height: 39px;
                    border: 1px solid #dadce0;
                    border-radius: 10px;
                    background: #fff;
                    color: #3c4043;
                    font-family: Arial, sans-serif;
                    font-size: 15px;
                    font-weight: 500;
                }

                .googleButton:hover { background: #f8faff; border-color: #c4c7c5; }
				.googleButton:disabled { opacity: .65; cursor: wait; }
				.googleMark { width: 20px; height: 20px; flex: 0 0 auto; }
				.authNotice { padding: 12px 14px; border-radius: 10px; font-size: 14px; line-height: 1.45; margin-bottom: 12px; text-align: left; }
				.authNoticeInfo { background: #eef7f1; border: 1px solid rgba(102,174,124,.35); color: #28553a; }
				.authNoticeError { background: #fff3f1; border: 1px solid rgba(201,91,64,.25); color: #8b3b2a; }
				.authenticatorHelp { color: #686767; margin: 12px 0 4px; }
				.authenticatorSteps { color: #686767; margin: 12px 0; padding-left: 24px; text-align: left; }
				.authenticatorSteps li { margin-bottom: 10px; padding-left: 4px; }
				.authenticatorStoreButtons { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 8px; }
				.storeButton { align-items: center; background: #000; border-radius: 7px; color: #fff !important; display: inline-flex; gap: 8px; min-height: 48px; padding: 7px 12px; text-decoration: none !important; }
				.storeButton svg { flex: 0 0 auto; font-size: 27px; }
				.storeButton span { display: flex; flex-direction: column; font-family: Arial, sans-serif; font-size: 17px; font-weight: 500; line-height: 1; }
				.storeButton small { font-size: 9px; font-weight: 400; letter-spacing: .02em; margin-bottom: 3px; }
				.authenticatorQrCode { display: block; width: 220px; height: 220px; margin: 12px auto; border-radius: 8px; }
				.authenticatorSecret { display: block; padding: 10px; border-radius: 6px; color: #28553a; background: #eef7f1; overflow-wrap: anywhere; }
				.accountChoice p { color: #26255C; margin-bottom: 18px; }
				.roleChoices { display: flex; gap: 12px; }
				button.roleChoice { flex: 1; min-height: 88px; padding: 12px; border: 1px solid #66AE7C; border-radius: 10px; background: transparent; color: #26255C; font-family: 'AvenirMedium', sans-serif; }
				.roleChoice:hover { background: #eef7f1; }
				.organizationChoices { flex-direction: column; }
				.backLink { margin-top: 18px; }
				@media (min-width: 768px) { .leftSide { display: block; } }
				@media (max-width: 600px) { .viewLogin { background: #fff; } .unifiedLogin { width: calc(100vw - 32px); } .custom-input, .formLinks { min-width: 0; width: 100%; } .roleChoices { flex-direction: column; } }
			`}</style>
		</div>
	);
};

export default UnifiedSignIn;
